import { useInfiniteQuery } from "@tanstack/react-query";
import axios from "axios";
import path from "constants/path";
import {
  convertPaperDetailsToPaper,
  getRelatedPapers,
  RelatedPapersType,
} from "helpers/api";
import { useRouter } from "next/router";
import { useMemo } from "react";

type UseRelatedPapersProps = {
  id: string;
  relatedPaperType: RelatedPapersType;
  enabled?: boolean;
};

export function useRelatedPapers({
  id,
  relatedPaperType,
  enabled = true,
}: UseRelatedPapersProps) {
  const router = useRouter();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: [relatedPaperType, id, "paginated"],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await getRelatedPapers(relatedPaperType, id, pageParam);
      if (!response || !response.papers) {
        return {
          papers: [],
          nextOffset: 0,
          isEnd: true,
        };
      }
      return {
        papers: response.papers.map(convertPaperDetailsToPaper),
        nextOffset: response.nextOffset,
        isEnd: response.isEnd,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.isEnd) {
        return undefined;
      }
      return lastPage.nextOffset;
    },
    enabled: enabled && Boolean(id),
    onError: (error: unknown) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 429) {
          router.push(path.TOO_MANY_REQUESTS);
        }
      }
    },
    staleTime: Infinity, // Citations unlikely to change during user visit
  });

  // Flatten all pages into a single array of papers
  const papers = useMemo(
    () => data?.pages.flatMap((page) => page.papers) ?? [],
    [data?.pages]
  );

  return {
    papers,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
  };
}
